import {
  resolveSiteAppearance,
  siteContrastRatio,
  type SiteAppearanceSettings,
  type SiteAppearanceTokens,
} from './site-appearance-contract';

/** SiteAppearanceTokens is a string-indexed record; TS correctly treats unchecked keys
 * as possibly absent. Enforce the resolver's required semantic contract at runtime. */
function requireSiteToken(tokens: SiteAppearanceTokens, key: string): string {
  const value = tokens[key];
  if (value === undefined) {
    throw new Error(`Missing required site appearance token: ${key}`);
  }
  return value;
}

/**
 * Translate the existing Site Appearance contract into Slider/FlexUI's management tokens.
 * Status colours and Presentation/Project Theme tokens are intentionally not touched.
 */
export function resolveManageAppearanceTokens(
  settings: SiteAppearanceSettings,
  systemDark: boolean,
): Record<string, string> {
  const { dark, tokens } = resolveSiteAppearance(settings, systemDark);
  const token = (key: string): string => requireSiteToken(tokens, key);
  const primary = token('--primary');
  const foreground = token('--foreground');
  const surface = token('--card');
  const softBackground = token('--muted');
  const navBackground = token('--site-nav-background');
  const softText = siteContrastRatio(primary, softBackground) >= 4.5
    ? primary : foreground;

  return {
    // FlexUI neutral controls consume CSS gray tokens independently of Slider.
    // Map only gray *colour values* (not the HSL-typed --background/--card/
    // --primary contracts) so dialogs, badges and table chrome match the
    // selected neutral palette. Status colours stay semantic.
    '--gray-50': token('--muted'),
    '--gray-100': 'color-mix(in srgb, ' + token('--muted') + ' 75%, ' + surface + ')',
    '--gray-200': token('--border'),
    '--gray-300': 'color-mix(in srgb, ' + token('--border') + ' 70%, ' + foreground + ')',
    '--gray-400': token('--muted-foreground'),
    '--gray-500': token('--muted-foreground'),
    '--gray-600': token('--muted-foreground'),
    '--gray-700': foreground,
    '--gray-800': foreground,
    '--gray-900': foreground,
    '--gray-950': foreground,
    // FlexUI Button (default/primary/text) uses the --primary-50..900 scale,
    // NOT --slider-primary. Keep the HSL-typed --primary itself untouched:
    // hsl(var(--primary)) is still used by other vendor components.
    // The scale must live at both /manage and document root for portals.
    '--primary-50': 'color-mix(in srgb, ' + primary + ' 8%, ' + surface + ')',
    '--primary-100': 'color-mix(in srgb, ' + primary + ' 16%, ' + surface + ')',
    '--primary-200': 'color-mix(in srgb, ' + primary + ' 28%, ' + surface + ')',
    '--primary-300': 'color-mix(in srgb, ' + primary + ' 46%, ' + surface + ')',
    '--primary-400': 'color-mix(in srgb, ' + primary + ' 90%, ' + foreground + ')',
    '--primary-500': primary,
    '--primary-600': 'color-mix(in srgb, ' + primary + ' 78%, ' + foreground + ')',
    '--primary-700': 'color-mix(in srgb, ' + primary + ' 68%, ' + foreground + ')',
    '--primary-800': 'color-mix(in srgb, ' + primary + ' 58%, ' + foreground + ')',
    '--primary-900': 'color-mix(in srgb, ' + primary + ' 48%, ' + foreground + ')',
    '--slider-hover-overlay': dark ? 'rgb(255 255 255 / 6%)' : 'rgb(18 24 38 / 5%)',
    '--slider-bg': token('--background'),
    '--slider-surface': surface,
    '--slider-surface-subtle': token('--muted'),
    '--slider-surface-hover': token('--muted'),
    '--slider-surface-raised': surface,
    '--slider-surface-overlay': surface,
    '--slider-border': token('--border'),
    '--slider-border-strong': 'color-mix(in srgb, ' + token('--border') + ' 65%, ' + foreground + ')',
    '--slider-primary': primary,
    '--slider-primary-foreground': token('--primary-foreground'),
    '--slider-button-foreground': token('--primary-foreground'),
    '--slider-primary-hover': 'color-mix(in srgb, ' + primary + ' 85%, ' + foreground + ')',
    '--slider-primary-soft': 'color-mix(in srgb, ' + primary + ' 12%, ' + surface + ')',
    '--slider-primary-soft-text': softText,
    '--slider-focus-ring': primary,
    '--slider-link': dark
      ? 'color-mix(in srgb, ' + primary + ' 45%, white)'
      : softText,
    '--slider-creative': primary,
    '--slider-accent': primary,
    '--slider-text-primary': foreground,
    '--slider-text-secondary': token('--muted-foreground'),
    '--slider-text-muted': token('--muted-foreground'),
    '--slider-sidebar-bg': surface,
    '--slider-sidebar-solid-bg': navBackground,
    '--slider-sidebar-outline-bg': token('--background'),
    '--slider-sidebar-border': token('--border'),
    '--slider-sidebar-text': token('--muted-foreground'),
    '--slider-sidebar-active-bg': token('--muted'),
    '--slider-sidebar-active-text': foreground,
    '--slider-sidebar-filled-bg': primary,
    '--slider-sidebar-filled-text': token('--primary-foreground'),
    '--slider-font-sans': token('--site-body-font'),
    '--fx-default-font-family': token('--site-body-font'),
    '--font-sans': token('--site-body-font'),
    '--slider-chart-primary': primary,
    '--slider-chart-muted': 'color-mix(in srgb, ' + primary + ' 38%, ' + surface + ')',
    '--slider-chart-grid': token('--border'),
    '--slider-chart-axis': token('--muted-foreground'),
    '--slider-shadow-hover': token('--site-shadow'),
    '--slider-shadow-raised': token('--site-shadow'),
    '--slider-shadow-overlay': dark ? '0 16px 40px rgb(0 0 0 / 48%)' : token('--site-shadow'),
  };
}

/**
 * Shared scope gets site tokens for panels and fonts; root gets only Slider aliases.
 * This reaches FlexUI portals without overwriting HSL tokens used by other libraries.
 */
export function resolveManageScopeTokens(
  settings: SiteAppearanceSettings,
  systemDark: boolean,
): SiteAppearanceTokens {
  const resolved = resolveSiteAppearance(settings, systemDark).tokens;
  // FlexUI and existing UI packages include HSL-typed --background/--card/--border
  // contracts. Injecting Site's HEX variables under those names breaks hsl(var(...)).
  // Site variables only belong in the Marketing/isolated-preview scope.
  const siteTokens = Object.fromEntries(
    Object.entries(resolved).filter(([name]) => name.startsWith('--site-')),
  );
  return {
    ...siteTokens,
    '--site-page-background': requireSiteToken(resolved, '--background'),
    '--site-page-foreground': requireSiteToken(resolved, '--foreground'),
    '--site-panel-background': requireSiteToken(resolved, '--card'),
    '--site-panel-border': requireSiteToken(resolved, '--border'),
    '--site-nav-background': requireSiteToken(resolved, '--site-nav-background'),
    '--site-primary-foreground': requireSiteToken(resolved, '--primary-foreground'),
    ...resolveManageAppearanceTokens(settings, systemDark),
  };
}

export function applyManagePortalTokens(
  root: HTMLElement,
  settings: SiteAppearanceSettings,
  systemDark: boolean,
  rtl = false,
): () => void {
  const { dark } = resolveSiteAppearance(settings, systemDark);
  const resolvedSite = resolveSiteAppearance(settings, systemDark).tokens;
  // Only expose site-prefixed variables at the document root. Do not override
  // non-Slider HSL contracts or Presentation/Project theme tokens.
  const tokens = {
    ...Object.fromEntries(Object.entries(resolvedSite).filter(([name]) => name.startsWith('--site-'))),
    ...resolveManageAppearanceTokens(settings, systemDark),
  };
  // Portals (Select/Dialog/Popover) render outside the scoped manage shell.
  // Keep their body typography consistent with the active UI locale.
  if (rtl) {
    const family = requireSiteToken(resolvedSite, '--site-persian-font');
    tokens['--slider-font-sans'] = family;
    tokens['--fx-default-font-family'] = family;
    tokens['--font-sans'] = family;
  }
  const previous = Object.keys(tokens).map((name) => ({
    name,
    value: root.style.getPropertyValue(name),
    priority: root.style.getPropertyPriority(name),
  }));
  const oldActive = root.getAttribute('data-manage-appearance-active');
  const oldButtonStyle = root.getAttribute('data-manage-appearance-button-style');
  const oldTheme = root.getAttribute('data-theme');
  const oldDarkClass = root.classList.contains('dark');
  const oldColorScheme = root.style.colorScheme;
  for (const [name, value] of Object.entries(tokens)) root.style.setProperty(name, value);
  root.setAttribute('data-manage-appearance-active', '');
  root.setAttribute('data-manage-appearance-button-style', settings.buttonStyle);
  root.setAttribute('data-theme', dark ? 'dark' : 'light');
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';

  return () => {
    for (const { name, value, priority } of previous) {
      if (value) root.style.setProperty(name, value, priority);
      else root.style.removeProperty(name);
    }
    if (oldActive === null) root.removeAttribute('data-manage-appearance-active');
    else root.setAttribute('data-manage-appearance-active', oldActive);
    if (oldButtonStyle === null) root.removeAttribute('data-manage-appearance-button-style');
    else root.setAttribute('data-manage-appearance-button-style', oldButtonStyle);
    if (oldTheme === null) root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', oldTheme);
    root.classList.toggle('dark', oldDarkClass);
    root.style.colorScheme = oldColorScheme;
  };
}
